import React, { useMemo, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import parse from 'html-react-parser';
import { Autocomplete, TextField, CircularProgress, MenuItem, Typography, Tabs, Tab, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useSearchContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import { warning } from '_lex/_foundations/phxColors';
import _ from 'lodash';

type SearchBarProps = {
  onClose?: () => void;
};

const SearchBar: React.FC<SearchBarProps> = ({ onClose }) => {
  const {
    getHighlightedText,
    searchResults,
    loading,
    setSelectedItem,
    setIsContentLoading,
    setSearchInputValue,
    searchInputValue,
    isSearchOpen,
    setIsSearchOpen,
    searchType,
    setSearchType,
  } = useSearchContext();
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState(searchInputValue);

  const debouncedSetSearchInputValue = useMemo(
    () =>
      debounce((value: string) => {
        setSearchInputValue(value);
      }, 500),
    [setSearchInputValue]
  );

  useEffect(() => {
    if (_.isEmpty(inputValue)) {
      setSearchInputValue('');
    } else {
      debouncedSetSearchInputValue(inputValue);
    }
  }, [inputValue, debouncedSetSearchInputValue, setSearchInputValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'issued' | 'draft') => {
    setSearchType(newValue);
    // Keep search input value but clear selected item and navigate away from current content
    setSelectedItem(null);
    // Ensure search is triggered for the new search type
    setIsSearchOpen(true);
  };

  return (
    <>
      <Stack spacing={2}>
        <Tabs
          value={searchType}
          onChange={handleTabChange}
          sx={{ mb: 2 }}
        >
          <Tab
            label={t('draftManuals.searchManuals.issuedTab')}
            value='issued'
          />
          <Tab
            label={t('draftManuals.searchManuals.draftTab')}
            value='draft'
          />
        </Tabs>
        <Autocomplete
          freeSolo
          autoFocus
          inputValue={inputValue || ''}
          options={searchResults || []}
          getOptionLabel={(option: any) => option.manualName || ''}
          filterOptions={(options) => options}
          loading={loading}
          onInputChange={(_, value) => {
            setInputValue(value);
          }}
          onFocus={() => setIsSearchOpen(true)}
          onBlur={() => setIsSearchOpen(false)}
          sx={{
            width: '100%',
            '& .MuiFormControl-root': {
              padding: 0,
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant='outlined'
              placeholder={t('draftManuals.searchManuals.inputPlaceholder')}
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress
                        color='inherit'
                        size={20}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem
              {...props}
              key={option.contentUri}
              onClick={() => {
                setSelectedItem(option);
                setIsContentLoading(true);
                navigate(`/admin/search-manuals?content=${encodeURIComponent(option.contentUri)}`);
                if (onClose) {
                  onClose();
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant='body1'
                  color='primary'
                >
                  {`${option.heading ? option.heading : ''} ${option.title}`}
                </Typography>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ ml: 1 }}
                >
                  {`(${option.manualName})`}
                </Typography>

                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '& strong': {
                      backgroundColor: warning[100],
                      padding: '0 2px',
                      borderRadius: 1,
                    },
                  }}
                >
                  {parse(getHighlightedText(option.highlights, 90))}
                </Typography>
              </Box>
            </MenuItem>
          )}
        />
      </Stack>
    </>
  );
};

export default SearchBar;
